import React, { useEffect, useRef, useState } from "react";
import FormDropDown from "../../../forms/DropDownList/FormDropDown";
import "./ExpeditionControls.scss";
import { convertMSToHMS } from "../../../../helpers/Dates";
import TabbedPanel from "../../../TabbedPanel/TabbedPanel";
import EXPEDITION_LIVE_STATE from "./ExpeditionLiveState";
import Toggle from "../../../forms/Toggle/Toggle";
import HMSTimeField from "../../../forms/HMSTimeField/HMSTimeField";
import FormField from "../../../forms/FormField/FormField";
import CollapsibleHeadedSection from "../../../CollapsibleHeadedSection/CollapsibleHeadedSection";

const timeoutIds = {};
const ExpeditionStateControls = (props) => {
    // A state variable to cause an update every second so we can show the live time
    const [tick, setTick] = useState(0);
    const forceUpdate = () => setTick((tick) => tick + 1);
    const [buttonClickedWaitingForUpdate, setButtonClickedWaitingForUpdate] = useState(false);
    const [lastUpdateReceived, setLastUpdateReceived] = useState(Date.now());

    useEffect(() => {
        setButtonClickedWaitingForUpdate(false);
    }, [props]);
    useEffect(() => {
        if (buttonClickedWaitingForUpdate) {
            clearTimeout(timeoutIds.buttonClickedWaitingForUpdate);
            timeoutIds.buttonClickedWaitingForUpdate = setTimeout(() => {
                setButtonClickedWaitingForUpdate(false);
            }, 1000);
        }
    }, [buttonClickedWaitingForUpdate]);

    const expeditionDataFetched = props.expeditionDataFetched;

    const primaryStream = props.featureVars.manageExpeditionControls.masterStream;
    const yourTeam = props.featureVars.manageExpeditionControls.selectedTeam;

    const [localStateData, setLocalStateData] = useState(null);
    const [rawDataForEdit, setRawDataForEdit] = useState('');
    const localStateDataRef = useRef(localStateData);
    const [lastRefresh, setLastRefresh] = useState(0);
    const refreshLocalStateData = () => {
        const newStateData = getStateData();
        setLocalStateData(newStateData);
        localStateDataRef.current = newStateData;
        setRawDataForEdit(JSON.stringify(newStateData, null, 4));
        setLastRefresh(Date.now());
        console.log('--- ExpeditionControls, refreshLocalStateData: ', newStateData);
    }

    const getStateData = () => {
        let stateData = null;
        if (props.previewControls && props?.passedInPreviewData?.expeditionState) {
            stateData = props.passedInPreviewData.expeditionState;
        }
        if (!props.previewControls && expeditionDataFetched) {
            stateData = EXPEDITION_LIVE_STATE.expeditionMainState;
        }
       /*  if (stateData === null) {
            stateData = props?.featureVars?.expeditionState;
        } */
        if (stateData) {
            // console.log('--- ExpeditionControls, stateData: ', stateData);
            return stateData;
        }
        // Default state data
        return {
            expeditionInited: false,
        };
    }

    const getOverlaysStateData = () => {
        let overlaysStateData = null;
        if (props.previewControls && props?.passedInPreviewData?.activeOverlays) {
            overlaysStateData = props.passedInPreviewData.activeOverlays;
        }
        if (overlaysStateData === null) {
            overlaysStateData = props?.featureVars?.activeOverlays;
        }
        if (overlaysStateData) {
            return overlaysStateData;
        }
        return {
            alerts: [],
            teamStats: [],
        };
    }

    const updateReceivedCallback = (newExpeditionState, sentData) => {
        // console.log('--- ExpeditionControls, updateReceivedCallback: ', data);
        setTick((tick) => tick + 1);
        setLastUpdateReceived(Date.now());
        setButtonClickedWaitingForUpdate(false);
    }
    useEffect(() => {
        const interval = setInterval(() => {
            setTick((tick) => tick + 1);
        }, 1000);
        EXPEDITION_LIVE_STATE.registerUpdateListener(updateReceivedCallback);
        refreshLocalStateData();
        console.log('--- ExpeditionControls, useEffect (mounted)', props);
        return () => {
            clearInterval(interval);
            EXPEDITION_LIVE_STATE.unregisterUpdateListener(updateReceivedCallback);
        }
    }, []);

    const stateData = getStateData();
    const overlaysStateData = getOverlaysStateData();
    const streamOverlayActive = overlaysStateData?.streamOverlayActive === true;
    // const HMSTotalTime = convertMSToHMS(Date.now() - stateData.featureStartTime, true);

    const featureIdForCommands = props.previewControls ? 'previewFrame' : props.featureId;

    useEffect(() => {
        // If the expedition state has changed, and we were waiting for an update, stop waiting
        if (buttonClickedWaitingForUpdate) {
            setButtonClickedWaitingForUpdate(false);
        }
    }, [stateData.expeditionInited, stateData.expeditionActive, stateData.expeditionStartTime]);
    
    const getDisplayFormattedTime = (timeInMS) => {
        if (timeInMS <= 0) return '00:00:00';
        const timeObj = convertMSToHMS(timeInMS, true);
        return `${timeObj.h}:${timeObj.m_str}:${timeObj.s_str}`;
    }

    const getTeamDropdownListData = () => {
        const listData = [];
        if (props.customisationData?.setup?.teams?.teams && props.customisationData.setup.teams.teams.length > 0) {
            for (let i = 0; i < props.customisationData.setup.teams.teams.length; i++) {
                listData.push({value: i, label: props.customisationData.setup.teams.teams[i].name});
            }
        }
        return listData;
    }
    const teamsListData = getTeamDropdownListData();

    const [selectedTeamIndex, setSelectedTeamIndex] = useState(0);
    const [teamData, setTeamData] = useState(null);
    const [memberData, setMemberData] = useState(null);
    // If the teams change, and our selected team index is now out of range, reset it to 0
    useEffect(() => {
        if (selectedTeamIndex >= teamsListData.length) {
            setSelectedTeamIndex(0);
        }
        setTeamData(props.customisationData?.setup?.teams?.teams?.[selectedTeamIndex] || null);
        const teamMembers = props.customisationData?.setup?.teamMembers?.teamMembers?.filter((member) => {
            return member.team === selectedTeamIndex;
        }) ?? null;
        setMemberData(teamMembers);
        // console.log('--- ExpeditionControls, teamData: ', teamData, memberData);
    }, [selectedTeamIndex, props.customisationData]);

    const [currentTab, setCurrentTab] = useState(0);

    const [autoShowTeamStatusAfterHero, setAutoShowTeamStatusAfterHero] = useState(false);
    const [autoShowDelay, setAutoShowDelay] = useState(10);

    const teamState = stateData.teamStates ? stateData.teamStates[selectedTeamIndex] : null;

    const journeyData = props.customisationData?.setup['journey' + (teamData?.journey + 1 ?? 0)];
    const journeyStagesCount = journeyData ? (journeyData.stages ? journeyData.stages.length : 0) : 0;
    const routeData = props.customisationData?.setup['route' + (teamState?.journeyStage + 1 ?? 0)];
    const routeWaypointsCount = routeData ? (routeData.waypoints.length > 0 ? routeData.waypoints.length : 0) : 0;

    const isOnFinalJourneyStage = teamState?.journeyStage === (journeyStagesCount - 1);

    const hasReachedEndOfStage = teamState?.currentWaypoint > routeWaypointsCount;
    const wayPointsReachedFlags = [];
    for (let i = 0; i < routeWaypointsCount; i++) {
        wayPointsReachedFlags.push(teamState?.currentWaypoint > i);
    }
    const checkpointToDisplay = hasReachedEndOfStage ? teamState?.journeyStage + 1 : teamState?.journeyStage;

    const selectedTeamHeroBannerActiveOverlay = overlaysStateData.teamStats.find((overlay) => {
        return overlay.isHero === true && overlay.teamIndex === selectedTeamIndex;
    });
    const canShowHeroBanner = !selectedTeamHeroBannerActiveOverlay ? true : false;
    const canHideHeroBanner = selectedTeamHeroBannerActiveOverlay ? true : false;

    const selectedTeamStatsActiveOverlay = overlaysStateData.teamStats.find((overlay) => {
        return overlay.isHero !== true && overlay.teamIndex === selectedTeamIndex;
    });
    const canShowStatsBanner = !selectedTeamStatsActiveOverlay ? true : false;
    const canHideStatsBanner = selectedTeamStatsActiveOverlay ? true : false;

    // console.log('--- ExpeditionControls: ', overlaysStateData);
    const waypointAlertActiveOverlay = overlaysStateData.alerts.find((overlay) => {
        return overlay.type === 'waypoint_reached_alert' && overlay.teamIndex === selectedTeamIndex;
    });
    const canAdvanceWaypoint_preview = stateData.expeditionActive && !waypointAlertActiveOverlay && teamState?.currentWaypoint <= routeWaypointsCount ? true : false;

    const isItemAlertOverlayActive = (itemIndex) => {
        const itemAlert = overlaysStateData.alerts.find((overlay) => {
            return overlay.type === 'item_used_alert' && overlay.teamIndex === selectedTeamIndex && overlay.itemIndex === itemIndex;
        });
        return itemAlert ? true : false;
    }

    // Challenges, each route consists of two challenges, one at the start and one at the end
    // A challenge is only available if we are on the first or last waypoint of a route
    // Completed challenges are stored in teamState.endedChallenges[] with a challengeIndex
    // The active challenge can be a number in which case we have to look it up or it can be an object with challengeIndex, startedTime and completedTime
    // If the active challenge is a number we need to look for a seperate challengeStartTime property in our expeditionState
    const startChallengeIndex = routeData?.routeSetup?.[0]?.startChallenge ?? 0;
    const startChallengeData = props.customisationData?.setup?.challenges?.challenges?.[startChallengeIndex] || null;
    const startChallengeAvailable = stateData.expeditionInited && stateData.expeditionActive && stateData.expeditionStartTime > 0 && teamState?.currentWaypoint === 0 ? true : false;
    let startChallengeActive = typeof teamState?.activeChallenge === 'number' ? (teamState.activeChallenge === startChallengeIndex ? true : false) : (teamState?.activeChallenge?.challengeIndex === startChallengeIndex ? true : false);
    let startChallengeStartedTime = 0;
    if (startChallengeActive) {
        startChallengeStartedTime = typeof teamState?.activeChallenge === 'number' ? (teamState?.activeChallengeStartTime ?? 0) : (teamState?.activeChallenge?.startedTime ?? 0);
    }
    let startChallengeEnded = false;
    let startChallengeSuccessful = false;
    let startChallengeEndedTime = 0;
    if (teamState?.endedChallenges && teamState.endedChallenges.length > 0) {
        const startChallengeState = teamState.endedChallenges.find((challenge) => challenge.challengeIndex === startChallengeIndex);
        if (startChallengeState) {
            startChallengeEnded = true;
            startChallengeSuccessful = startChallengeState.successful;
            startChallengeStartedTime = startChallengeState.startedTime;
            startChallengeEndedTime = startChallengeState.completedTime;
        }
    }
    let startChallengeColourCss = '';
    let startChallengeStatusColourCss = ' ch-available-to-start-text';
    let startChallengeStatusText = primaryStream ? 'Available to Start' : 'Only the Primary Stream can start this';
    if (!startChallengeAvailable) {
        startChallengeColourCss = ' not-currently-available';
        startChallengeStatusText = 'Not Currently Available';
    }
    if (startChallengeAvailable && startChallengeActive) {
        startChallengeColourCss = ' in-progress';
        startChallengeStatusText = 'In Progress';
        startChallengeStatusColourCss = ' attention-text';
    }
    if (startChallengeEnded && startChallengeSuccessful) {
        startChallengeColourCss = ' completed';
        startChallengeStatusText = 'Completed Successfully';
        startChallengeStatusColourCss = ' confirm-text';
    }
    if (startChallengeEnded && !startChallengeSuccessful) {
        startChallengeColourCss = ' failed';
        startChallengeStatusText = 'Failed';
        startChallengeStatusColourCss = ' warning-text';
    }


    const endChallengeIndex = routeData?.routeSetup?.[0]?.endChallenge ?? 0;
    const endChallengeData = props.customisationData?.setup?.challenges?.challenges?.[endChallengeIndex] || null;
    const endChallengeAvailable = teamState?.currentWaypoint === routeWaypointsCount + 1 ? true : false;
    let endChallengeActive = typeof teamState?.activeChallenge === 'number' ? (teamState.activeChallenge === endChallengeIndex ? true : false) : (teamState?.activeChallenge?.challengeIndex === endChallengeIndex ? true : false);
    let endChallengeStartedTime = 0;
    if (endChallengeActive) {
        endChallengeStartedTime = typeof teamState?.activeChallenge === 'number' ? (teamState?.activeChallengeStartTime ?? 0) : (teamState?.activeChallenge?.startedTime ?? 0);
    }
    let endChallengeEnded = false;
    let endChallengeSuccessful = false;
    let endChallengeEndedTime = 0;
    if (teamState?.endedChallenges && teamState.endedChallenges.length > 0) {
        const endChallengeState = teamState.endedChallenges.find((challenge) => challenge.challengeIndex === endChallengeIndex);
        if (endChallengeState) {
            endChallengeEnded = true;
            endChallengeSuccessful = endChallengeState.successful;
            endChallengeStartedTime = endChallengeState.startedTime;
            endChallengeEndedTime = endChallengeState.completedTime;
        }
    }
    let endChallengeColourCss = '';
    let endChallengeStatusColourCss = '';
    let endChallengeStatusText = primaryStream ? 'Available to Start' : 'Only the Primary Stream can start this';
    if (!endChallengeAvailable) {
        endChallengeColourCss = ' not-currently-available';
        endChallengeStatusText = 'Not Currently Available';
    }
    if (endChallengeAvailable && endChallengeActive) {
        endChallengeColourCss = ' in-progress';
        endChallengeStatusText = 'In Progress';
        endChallengeStatusColourCss = ' attention-text';
    }
    if (endChallengeEnded && endChallengeSuccessful) {
        endChallengeColourCss = ' completed';
        endChallengeStatusText = 'Completed Successfully';
        endChallengeStatusColourCss = ' confirm-text';
    }
    if (endChallengeEnded && !endChallengeSuccessful) {
        endChallengeColourCss = ' failed';
        endChallengeStatusText = 'Failed';
        endChallengeStatusColourCss = ' warning-text';
    }

    const [compassLockTime, setCompassLockTime] = useState(props.featureVars?.expeditionMessagesControls?.compassLockTime ?? 120);
    const isCompassLockedOverlayActive = () => {
        const compassLockAlert = overlaysStateData.alerts.find((overlay) => {
            return overlay.type === 'compass_frozen_alert' && overlay.teamIndex === selectedTeamIndex;
        });
        return compassLockAlert ? true : false;
    }

    const outOfTime = stateData.expeditionCompletedTime === 0 && stateData.expeditionTargetEndTime > 0 && (Date.now() / 1000) >= stateData.expeditionTargetEndTime;
    const expeditionCompleted = stateData.expeditionCompletedTime > 0;
    const teamCompletedExpedition = teamState?.expeditionCompleted === true;

    // Quite an if statement!
    let canBacktrackJourney = stateData && stateData.expeditionInited && stateData.expeditionActive && stateData.expeditionStartTime > 0 && stateData.teamStates && stateData.teamStates.length > 0 && (stateData.teamStates[selectedTeamIndex].journeyStage > 0 || stateData.teamStates[selectedTeamIndex].currentWaypoint > 0);
    let backtrackJourneyStageTo = canBacktrackJourney && stateData?.teamStates?.[selectedTeamIndex]?.currentWaypoint === 0 ? stateData.teamStates[selectedTeamIndex].journeyStage - 1 : stateData?.teamStates?.[selectedTeamIndex]?.journeyStage ?? 0;
    let backTrackWaypointTo = stateData?.teamStates?.[selectedTeamIndex]?.currentWaypoint > 0 ? stateData?.teamStates?.[selectedTeamIndex]?.currentWaypoint - 1 : -1;
    if (backTrackWaypointTo === -1 && backtrackJourneyStageTo >= 0) {
        backTrackWaypointTo =  props.customisationData?.setup['route' + (journeyData?.stages?.[backtrackJourneyStageTo]?.route + 1)]?.waypoints?.length + 1 ?? 0;
    }
    if (canBacktrackJourney && (backtrackJourneyStageTo < 0 || backTrackWaypointTo < 0)) {
        canBacktrackJourney = false;
    }
    let canAdvanceWaypoint_live = stateData.expeditionInited && stateData.expeditionActive && stateData.expeditionStartTime > 0 && stateData.teamStates && stateData.teamStates.length > 0 && teamState && routeData && teamState.currentWaypoint <= routeWaypointsCount + 1 && !outOfTime && !expeditionCompleted && !teamCompletedExpedition;

    const advanceWaypoint_live = () => {
        if (canAdvanceWaypoint_live && primaryStream && stateData.expeditionActive) {
            const nextWaypoint = teamState.currentWaypoint + 1;
            if (nextWaypoint > routeWaypointsCount + 1) {
                const nextJourneyStage = teamState.journeyStage + 1;
                if (nextJourneyStage < journeyStagesCount) {
                    EXPEDITION_LIVE_STATE.silentlySetJourneyStageForTeam(selectedTeamIndex, nextJourneyStage, 0);
                } else {
                    return;
                }
            } else
            if (nextWaypoint > routeWaypointsCount) {
                EXPEDITION_LIVE_STATE.checkpointReachedForTeam(selectedTeamIndex, teamState.journeyStage, routeWaypointsCount + 1);
            } else {
                EXPEDITION_LIVE_STATE.waypointReachedForTeam(selectedTeamIndex, teamState.journeyStage, nextWaypoint);
            }
        }
    }

    const getChallengeIndexForWaypoint = (teamIndex, journeyStageIndex, waypointIndex) => {
        const journeyData = props.customisationData?.setup['journey' + (teamData?.journey + 1 ?? 0)];
        const routeData = props.customisationData?.setup['route' + (journeyData?.stages?.[journeyStageIndex]?.route + 1)];
        
        if (routeData) {
            if (waypointIndex === 0) {
                return routeData?.routeSetup?.[0]?.startChallenge ?? null;
            } else
            if (waypointIndex >= routeData.waypoints.length + 1) {
                return routeData?.routeSetup?.[0]?.endChallenge ?? null;
            }
        }
        return null;
    }

    return (
        <div className="generic-feature-status fl-column" style={{marginBottom: '-1rem'}}>
            {(stateData === null || !stateData.expeditionInited) &&
                <div className="bold-text warning-text">
                    Your expedition needs to be initialized.
                </div>
            }
            {stateData !== null && stateData.expeditionInited && !stateData.expeditionActive && stateData.expeditionStartTime === 0 &&
                <div className="bold-text attention-text">
                    Expedition is initialized but has not been started.
                </div>
            }
            {stateData !== null && stateData.expeditionInited && !stateData.expeditionActive && stateData.expeditionStartTime !== 0 &&
                <div className="bold-text attention-text">
                    Expedition has ended.
                </div>
            }
            {stateData !== null && stateData.expeditionInited && stateData.expeditionActive && stateData.expeditionStartTime !== 0 &&
                <div className="bold-text confirm-text">
                    Expedition is currently live.
                </div>
            }
            <div className="fl-column compact">
                <div className="fl-column no-gap">
                    <div className="fl-row light-text">
                        <div className="bold-text">Current Time:</div>
                        <div>{new Date(Date.now()).toLocaleTimeString()}</div>
                    </div>
                    <div className="fl-row light-text">
                        <div className="bold-text">Last Update:</div>
                        <div>{new Date(lastUpdateReceived).toLocaleTimeString()}</div>
                    </div>
                    <div className="fl-row light-text">
                        <div className="bold-text">Last Refresh:</div>
                        <div>{new Date(lastRefresh).toLocaleTimeString()}</div>
                    </div>
                </div>
                {primaryStream &&
                    <div className="fl-row bold-text attention-text">
                        You should refresh your data before making edits.
                    </div>
                }
                {!primaryStream &&
                    <div className="fl-row bold-text">
                        You are not on the primary stream, you can view the data but cannot make changes.
                    </div>
                }
                <div className="button-grid">
                    <button className={`standard-button ${!buttonClickedWaitingForUpdate ? 'live-but-green' : 'button-inactive'}`} 
                        onClick={
                            buttonClickedWaitingForUpdate ? null :
                            () => {
                                setButtonClickedWaitingForUpdate(true);
                                refreshLocalStateData();
                                setTimeout(() => {
                                    setButtonClickedWaitingForUpdate(false);
                                }, 1000);
                            }
                        }
                    >
                        Refresh Now
                    </button>
                </div>
                {primaryStream &&
                    <>
                        <div className="fl-row bold-text attention-text">
                            Some state updates may not be reflected until we receive a heartbeat. Note: this will force a heartbeat to be immediately sent to all clients!
                        </div>
                        <div className="button-grid">
                            <button className={`standard-button ${!buttonClickedWaitingForUpdate ? 'live-but-orange' : 'button-inactive'}`} 
                                onClick={
                                    buttonClickedWaitingForUpdate ? null :
                                    () => {
                                        setButtonClickedWaitingForUpdate(true);
                                        EXPEDITION_LIVE_STATE.adminForceHeartbeat();
                                    }
                                }
                            >
                                Force Heartbeat Now
                            </button>
                        </div>
                    </>
                }
            </div>
            {stateData.expeditionInited &&
                <TabbedPanel
                    tabs={[{label: "Expedition"}, {label: "Teams"}, {label: "Raw Data"}]}
                    selectedTab={0}
                    switchTabCallback={(index) => setCurrentTab(index)}
                >
                    <div className="expoc-demargin-tab-content" style={{marginBottom: '-1rem'}}>
                        {currentTab === 2 &&
                            <div className="fl-column compact">
                                <div className="fl-row bold-text warning-text">
                                    This is a locally stored copy of the expedition data, ensure you refresh your data before making edits. Extra care should be taken when directly editing data!.
                                </div>
                                <textarea
                                    value={rawDataForEdit}
                                    onChange={(e) => setRawDataForEdit(e.target.value)}
                                    rows={10}
                                    cols={50}
                                />
                                <hr />
                                <div className="button-grid">
                                    <button className={`standard-button ${!buttonClickedWaitingForUpdate && primaryStream ? 'live-but-red' : 'button-inactive'}`} 
                                        onClick={
                                            (buttonClickedWaitingForUpdate || !primaryStream) ? null :
                                            () => {
                                                console.log('--- ExpeditionControls, raw data to send: ', rawDataForEdit);
                                                let parsedData = null;
                                                try {
                                                    parsedData = JSON.parse(rawDataForEdit);
                                                    console.log('--- ExpeditionControls, parsed data: ', parsedData);
                                                    if (parsedData) {
                                                        setButtonClickedWaitingForUpdate(true);
                                                        EXPEDITION_LIVE_STATE.adminSetEntireExpeditionState(parsedData);
                                                    }
                                                } catch (e) {
                                                    console.error('--- ExpeditionControls, raw data is not valid JSON: ', e);
                                                    alert('The data is not valid JSON, please correct any errors before saving.');
                                                    return;
                                                }
                                            }
                                        }
                                    >
                                        Save Changes Now
                                    </button>
                                </div>
                            </div>
                        }
                        {currentTab === 0 &&
                            <div className="fl-column compact">
                                <div className="fl-column no-gap">
                                    <div className="fl-row light-text">
                                        <div className="bold-text">Start Time:</div>
                                        <div>{stateData.expeditionStartTime ? new Date(stateData.expeditionStartTime * 1000).toLocaleTimeString() : 'Not Started'}</div>
                                    </div>
                                    <div className="fl-row light-text">
                                        <div className="bold-text">Time Allowed:</div>
                                        <div>{stateData.expeditionTimeAllowed ? getDisplayFormattedTime(stateData.expeditionTimeAllowed * 1000) : 'N/a'}</div>
                                    </div>
                                    <div className="fl-row light-text">
                                        <div className="bold-text">End Time:</div>
                                        <div>{stateData.expeditionTargetEndTime ? new Date(stateData.expeditionTargetEndTime * 1000).toLocaleTimeString() : 'N/a'}</div>
                                    </div>
                                    <div className="fl-row light-text">
                                        <div className="bold-text">Completed Time:</div>
                                        <div>{stateData.expeditionCompletedTime ? new Date(stateData.expeditionCompletedTime * 1000).toLocaleTimeString() : 'N/a'}</div>
                                    </div>
                                </div>
                                <hr />
                                {(!stateData.allTeamsChatUnlocked || stateData.allTeamsChatUnlockedUntil < Date.now() / 1000) &&
                                    <div className="fl-column compact">
                                        <div className="bold-text attention-text">
                                            Chat is not currently unlocked for all teams. You can unlock it from the Messaging Controls section.
                                        </div>
                                    </div>
                                }
                                {stateData.allTeamsChatUnlocked && stateData.allTeamsChatUnlockedUntil > Date.now() / 1000 &&
                                    <div className="fl-column compact">
                                        <div className="bold-text confirm-text">
                                            Chat is currently unlocked for all teams until {stateData.allTeamsChatUnlockedUntil ? new Date(stateData.allTeamsChatUnlockedUntil * 1000).toLocaleTimeString() : 'N/a'}.
                                        </div>
                                        <div className="button-grid">
                                            <button className={`standard-button ${!buttonClickedWaitingForUpdate ? 'live-but-green' : 'button-inactive'}`} 
                                                onClick={
                                                    buttonClickedWaitingForUpdate ? null :
                                                    () => {
                                                        console.log('--- ExpeditionControls, reshow chat unlocked alert');
                                                        props.sendFeatureCommandFunc(null, "admin_showAllChatUnlockedAlert", featureIdForCommands, null);
                                                        setButtonClickedWaitingForUpdate(true);
                                                        setTimeout(() => {
                                                            setButtonClickedWaitingForUpdate(false);
                                                        }, 1000);
                                                    }
                                                }
                                            >
                                                Show Chat Unlocked Overlay
                                            </button>
                                        </div>
                                        <div className="button-grid">
                                            <button className={`standard-button ${!buttonClickedWaitingForUpdate && primaryStream ? 'live-but-red' : 'button-inactive'}`} 
                                                onClick={
                                                    (buttonClickedWaitingForUpdate || !primaryStream) ? null :
                                                    () => {
                                                        console.log('--- ExpeditionControls, lock chat now');
                                                        EXPEDITION_LIVE_STATE.adminLockAllChat();
                                                    }
                                                }
                                            >
                                                Lock Chat Now
                                            </button>
                                        </div>
                                    </div>
                                }
                            </div>
                        }
                        {currentTab === 1 &&
                            <div className="fl-column">
                                {!localStateData.teamStates &&
                                    <div className="bold-text warning-text">
                                        Please refresh your data!
                                    </div>
                                }
                            {localStateData.teamStates &&
                                <>
                                <FormDropDown
                                    id="teamdropdown"
                                    label={"Select Team To View"}
                                    items={teamsListData}
                                    value={selectedTeamIndex}
                                    selectFunc={
                                        (e, id, item) => {
                                            setSelectedTeamIndex(item.value);
                                            // props.setFunc(item.index)
                                            // props.changeValFunc('pollControl', 'selectedPollType', pollTypes[item.index]);
                                        }
                                    }
                                    currentIndex={selectedTeamIndex}
                                    showErrors={false}
                                    enabled={teamsListData.length > 0}
                                />
                                <div className="fl-column compact">
                                    <div className="fl-row">
                                        <div className={`expoc-team-name bold-text ${(primaryStream || yourTeam === selectedTeamIndex) ? 'confirm-text' : 'warning-text'}`}>{teamsListData[selectedTeamIndex]?.label}</div>
                                        <div className="grow"></div>
                                        <div className={`bold-text ${(primaryStream || yourTeam === selectedTeamIndex) ? 'confirm-text' : 'warning-text'}`}>{stateData?.teamStates[selectedTeamIndex]?.totalXp}xp</div>
                                    </div>
                                    {primaryStream &&
                                        <div className="button-grid">
                                            <button className={`standard-button ${!buttonClickedWaitingForUpdate ? 'live-but-orange' : 'button-inactive'}`} 
                                                onClick={
                                                    buttonClickedWaitingForUpdate ? null :
                                                    () => {
                                                        setButtonClickedWaitingForUpdate(true);
                                                        EXPEDITION_LIVE_STATE.adminForceTeamUpdate(selectedTeamIndex);
                                                    }
                                                }
                                            >
                                                Force Team Phone Update From Server
                                            </button>
                                        </div>
                                    }
                                    <div className="fl-row bold-text warning-text">
                                        All edits are made to a locally stored copy of the expedition data, ensure you refresh your data before making changes.
                                    </div>
                                    <div className="fl-column no-gap">
                                        <div className="form-field-content label">Set XP:</div>
                                        <div className="fl-row">
                                            <FormField
                                                id="setxp"
                                                label="Set XP"
                                                showLabel={false}
                                                showError={false}
                                                type="number"
                                                value={localStateData?.teamStates?.[selectedTeamIndex]?.totalXp ?? 0}
                                                placeholder={'xp'}
                                                setFunc={(value) => {
                                                    localStateData.teamStates[selectedTeamIndex].totalXp = value;
                                                    forceUpdate();
                                                }}
                                            />

                                            <button className={`standard-button tight ${primaryStream && !buttonClickedWaitingForUpdate ? 'live-but-orange' : 'button-inactive'}`} 
                                                onClick={
                                                    (buttonClickedWaitingForUpdate || !primaryStream) ? null : () => {
                                                        console.log('--- ExpeditionControls, set team xp: ', localStateData.teamStates[selectedTeamIndex]);
                                                        EXPEDITION_LIVE_STATE.adminUpdateTeamXp(selectedTeamIndex, localStateData.teamStates[selectedTeamIndex].totalXp);
                                                    }
                                                }
                                            >
                                                Update XP
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <CollapsibleHeadedSection title="Journey">
                                    <>
                                        <div className="fl-column">
                                            <div className="fl-column no-gap">
                                                <div className="fl-row light-text">
                                                    <div className="bold-text">Journey Stage:</div>
                                                    <div>{stateData.teamStates[selectedTeamIndex]?.journeyStage ?? 'Unknown'}</div>
                                                </div>
                                                <div className="fl-row light-text">
                                                    <div className="bold-text">Waypoint:</div>
                                                    <div>{stateData.teamStates[selectedTeamIndex]?.currentWaypoint ?? 'Unknown'}</div>
                                                </div>
                                            </div>
                                            <div className="fl-row">
                                                <FormField
                                                    id="setstage"
                                                    label="Set Stage"
                                                    showLabel={true}
                                                    showError={false}
                                                    type="number"
                                                    value={localStateData?.teamStates[selectedTeamIndex]?.journeyStage ?? 0}
                                                    placeholder={'stage'}
                                                    setFunc={(value) => {
                                                        localStateData.teamStates[selectedTeamIndex].journeyStage = Number(value);
                                                        forceUpdate();
                                                    }}
                                                />
                                                <FormField
                                                    id="setwaypoint"
                                                    label="Set Waypoint"
                                                    showLabel={true}
                                                    showError={false}
                                                    type="number"
                                                    value={localStateData?.teamStates[selectedTeamIndex]?.currentWaypoint ?? 0}
                                                    placeholder={'waypoint'}
                                                    setFunc={(value) => {
                                                        localStateData.teamStates[selectedTeamIndex].currentWaypoint = Number(value);
                                                        forceUpdate();
                                                    }}
                                                />
                                            </div>
                                            <button className={`standard-button tight ${primaryStream && !buttonClickedWaitingForUpdate ? 'live-but-orange' : 'button-inactive'}`} 
                                                onClick={
                                                    (buttonClickedWaitingForUpdate || !primaryStream) ? null : () => {
                                                        console.log('--- ExpeditionControls, update journey data: ', localStateData.teamStates[selectedTeamIndex]);
                                                        EXPEDITION_LIVE_STATE.silentlySetJourneyStageForTeam(selectedTeamIndex, localStateData.teamStates[selectedTeamIndex].journeyStage, localStateData.teamStates[selectedTeamIndex].currentWaypoint, getChallengeIndexForWaypoint(selectedTeamIndex, localStateData.teamStates[selectedTeamIndex].journeyStage, localStateData.teamStates[selectedTeamIndex].currentWaypoint));
                                                    }
                                                }
                                            >
                                                Update Journey Data
                                            </button>
                                        </div>
                                        
                                        <div className="fl-column compact">
                                            <div className="fl-column no-gap">
                                                <div className="fl-row light-text">
                                                    <div className="bold-text">Active Challenge:</div>
                                                    <div>{stateData.teamStates[selectedTeamIndex]?.activeChallenge ?? 'None'}</div>
                                                </div>
                                            </div>
                                            <button className={`standard-button tight ${!buttonClickedWaitingForUpdate && stateData.teamStates[selectedTeamIndex]?.activeChallenge !== null && stateData.teamStates[selectedTeamIndex]?.activeChallengeStartTime > 0 ? 'live-but-green' : 'button-inactive'}`} 
                                                onClick={
                                                    buttonClickedWaitingForUpdate || stateData.teamStates[selectedTeamIndex]?.activeChallenge === null || stateData.teamStates[selectedTeamIndex]?.activeChallengeStartTime <= 0 ? null :
                                                    () => {
                                                        console.log('--- ExpeditionControls, reshow chat unlocked alert');
                                                        props.sendFeatureCommandFunc(null, "admin_showCurrentChallengeAlert", featureIdForCommands, {teamIndex: selectedTeamIndex});
                                                        setButtonClickedWaitingForUpdate(true);
                                                        setTimeout(() => {
                                                            setButtonClickedWaitingForUpdate(false);
                                                        }, 1000);
                                                    }
                                                }
                                            >
                                                Show Current Challenge Overlay
                                            </button>
                                        </div>
                                    </>
                                </CollapsibleHeadedSection>

                                <CollapsibleHeadedSection title="Chat and Compass">
                                    <>
                                        {(stateData.teamStates[selectedTeamIndex]?.chatUnlockedUntil < Date.now() / 1000) &&
                                            <div className="fl-column compact">
                                                <div className="bold-text attention-text">
                                                    Chat is not currently unlocked for this team. You can unlock it from the Messaging Controls section.
                                                </div>
                                            </div>
                                        }
                                        {(stateData.teamStates[selectedTeamIndex]?.chatUnlockedUntil > Date.now() / 1000) &&
                                            <div className="fl-column compact">
                                                <div className="bold-text confirm-text">
                                                    Chat is currently unlocked for this team until {stateData.teamStates[selectedTeamIndex]?.chatUnlockedUntil ? new Date(stateData.teamStates[selectedTeamIndex]?.chatUnlockedUntil * 1000).toLocaleTimeString() : 'N/a'}.
                                                </div>
                                                <div className="button-grid">
                                                    <button className={`standard-button ${!buttonClickedWaitingForUpdate ? 'live-but-green' : 'button-inactive'}`} 
                                                        onClick={
                                                            buttonClickedWaitingForUpdate ? null :
                                                            () => {
                                                                console.log('--- ExpeditionControls, reshow chat unlocked alert');
                                                                props.sendFeatureCommandFunc(null, "admin_showTeamChatUnlockedAlert", featureIdForCommands, {teamIndex: selectedTeamIndex});
                                                                setButtonClickedWaitingForUpdate(true);
                                                                setTimeout(() => {
                                                                    setButtonClickedWaitingForUpdate(false);
                                                                }, 1000);
                                                            }
                                                        }
                                                    >
                                                        Show Chat Unlocked Overlay
                                                    </button>
                                                </div>
                                                <div className="button-grid">
                                                    <button className={`standard-button ${!buttonClickedWaitingForUpdate && primaryStream ? 'live-but-red' : 'button-inactive'}`} 
                                                        onClick={
                                                            (buttonClickedWaitingForUpdate || !primaryStream) ? null :
                                                            () => {
                                                                console.log('--- ExpeditionControls, lock chat now');
                                                                EXPEDITION_LIVE_STATE.adminLockTeamChat(selectedTeamIndex);
                                                            }
                                                        }
                                                    >
                                                        Lock Chat Now
                                                    </button>
                                                </div>
                                            </div>
                                        }
                                        
                                        {(stateData.teamStates[selectedTeamIndex]?.compassLockedUntil < Date.now() / 1000) &&
                                            <div className="fl-column compact">
                                                <div className="bold-text attention-text">
                                                    Compass is not currently frozen for this team. You can freeze it from the Expedition Controls section.
                                                </div>
                                            </div>
                                        }
                                        {(stateData.teamStates[selectedTeamIndex]?.compassLockedUntil > Date.now() / 1000) &&
                                            <div className="fl-column compact">
                                                <div className="bold-text confirm-text">
                                                    Compass is currently frozen for this team until {stateData.teamStates[selectedTeamIndex]?.compassLockedUntil ? new Date(stateData.teamStates[selectedTeamIndex]?.compassLockedUntil * 1000).toLocaleTimeString() : 'N/a'}.
                                                </div>
                                                <div className="button-grid">
                                                    <button className={`standard-button ${!buttonClickedWaitingForUpdate ? 'live-but-green' : 'button-inactive'}`} 
                                                        onClick={
                                                            buttonClickedWaitingForUpdate ? null :
                                                            () => {
                                                                console.log('--- ExpeditionControls, reshow compass locked alert');
                                                                props.sendFeatureCommandFunc(null, "admin_showCompassFrozenAlert", featureIdForCommands, {teamIndex: selectedTeamIndex});
                                                                setButtonClickedWaitingForUpdate(true);
                                                                setTimeout(() => {
                                                                    setButtonClickedWaitingForUpdate(false);
                                                                }, 1000);
                                                            }
                                                        }
                                                    >
                                                        Show Compass Frozen Overlay
                                                    </button>
                                                </div>
                                                <div className="button-grid">
                                                    <button className={`standard-button ${!buttonClickedWaitingForUpdate && primaryStream ? 'live-but-red' : 'button-inactive'}`} 
                                                        onClick={
                                                            (buttonClickedWaitingForUpdate || !primaryStream) ? null :
                                                            () => {
                                                                console.log('--- ExpeditionControls, unfreeze compass now');
                                                                EXPEDITION_LIVE_STATE.adminUnlockTeamCompass(selectedTeamIndex);
                                                            }
                                                        }
                                                    >
                                                        Unfreeze Compass Now
                                                    </button>
                                                </div>
                                            </div>
                                        }
                                    </>
                                </CollapsibleHeadedSection>
                            </>
                            }
                            </div>
                        }
                    </div>
                </TabbedPanel>
            }
        </div>
    );
}

export default ExpeditionStateControls;
